const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: VolumeOffIcon } = require('@mui/icons-material/VolumeOff');
const { default: VolumeUpIcon } = require('@mui/icons-material/VolumeUp');

const {
    useState,
    useRef,
    useEffect,
    useCallback
} = React;

const internals = {};

internals.YOUTUBE_IFRAME_ALLOW = 'autoplay; encrypted-media; clipboard-write';
internals.YOUTUBE_CONTROL_MESSAGE_SOURCE = 'nearpeer-youtube-control';
internals.DEFAULT_YOUTUBE_PLAYER_SETTINGS = {
    autoplay: '1',
    mute: '1',
    loop: '1',
    playsinline: '1',
    controls: '1'
};

const YouTubePlayer = module.exports = function YouTubePlayer(props) {

    const {
        Player,
        PlayerFrame,
        SoundButton,
        YOUTUBE_IFRAME_ALLOW,
        getYouTubeEmbedUrl,
        isCordovaRuntime,
        registerYouTubePlayerControl,
        sendYouTubePlayerCommand
    } = internals;
    const {
        url,
        title = 'YouTube video',
        aspectRatio = '16 / 9',
        autoPlay = true,
        active = true,
        playerId = 'nearpeer-youtube-player',
        onLoad,
        className
    } = props;

    const playerSettings = internals.getPlayerSettings({ autoPlay });
    const embedUrl = getYouTubeEmbedUrl(url, { playerSettings });
    const [isMuted, setIsMuted] = useState(autoPlay);
    const iframeRef = useRef(null);
    const controlCleanupRef = useRef(null);
    const activeRef = useRef(active);

    activeRef.current = active;

    useEffect(() => {

        setIsMuted(autoPlay);
    }, [autoPlay, embedUrl]);

    useEffect(() => () => {

        if (controlCleanupRef.current) {
            controlCleanupRef.current();
            controlCleanupRef.current = null;
        }
    }, []);

    useEffect(() => {

        if (!embedUrl || !autoPlay) {
            return undefined;
        }

        const handleMessage = (event) => {

            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            const data = internals.parseYouTubePlayerMessage(event);

            if (!data) {
                return;
            }

            if (data.event === 'onReady') {
                if (activeRef.current) {
                    sendYouTubePlayerCommand(iframeRef.current, 'playVideo');
                }
                else {
                    sendYouTubePlayerCommand(iframeRef.current, 'pauseVideo');
                    sendYouTubePlayerCommand(iframeRef.current, 'seekToStart');
                }
            }

            if (data.event === 'infoDelivery' && typeof data.info?.muted === 'boolean') {
                setIsMuted(data.info.muted);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => window.removeEventListener('message', handleMessage);
    }, [embedUrl, autoPlay]);

    const handleIframeLoad = useCallback(() => {

        if (controlCleanupRef.current) {
            controlCleanupRef.current();
        }

        controlCleanupRef.current = registerYouTubePlayerControl(
            iframeRef.current,
            autoPlay
                ? (activeRef.current ? 'playVideo' : 'pauseVideo')
                : undefined
        );

        if (autoPlay && !activeRef.current) {
            sendYouTubePlayerCommand(iframeRef.current, 'seekToStart');
        }

        onLoad?.();
    }, [autoPlay, onLoad]);

    useEffect(() => {

        if (!embedUrl || !autoPlay) {
            return;
        }

        if (controlCleanupRef.current) {
            controlCleanupRef.current();
        }

        controlCleanupRef.current = registerYouTubePlayerControl(
            iframeRef.current,
            active ? 'playVideo' : ['pauseVideo', 'seekToStart']
        );
    }, [active, autoPlay, embedUrl]);

    const handleSoundToggle = useCallback(() => {

        const iframe = iframeRef.current;

        if (!iframe) {
            return;
        }

        const nextMuted = !isMuted;

        registerYouTubePlayerControl(iframe);
        sendYouTubePlayerCommand(iframe, nextMuted ? 'mute' : 'unMute');

        if (!nextMuted) {
            sendYouTubePlayerCommand(iframe, 'playVideo');
        }

        setIsMuted(nextMuted);
    }, [isMuted]);

    if (!embedUrl) {
        return null;
    }

    return (
        <Player className={className} $aspectRatio={aspectRatio}>
            <PlayerFrame
                id={playerId}
                ref={iframeRef}
                src={embedUrl}
                allow={YOUTUBE_IFRAME_ALLOW}
                allowFullScreen
                aria-hidden={!active}
                tabIndex={active ? undefined : -1}
                onLoad={handleIframeLoad}
                referrerPolicy={isCordovaRuntime() ? 'origin' : 'strict-origin-when-cross-origin'}
                title={title}
            />
            {autoPlay && active && (
                <SoundButton
                    onClick={handleSoundToggle}
                    aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                    title={isMuted ? 'Unmute video' : 'Mute video'}
                >
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </SoundButton>
            )}
        </Player>
    );
};

YouTubePlayer.propTypes = {
    url: T.string.isRequired,
    title: T.string,
    aspectRatio: T.string,
    autoPlay: T.bool,
    active: T.bool,
    playerId: T.string,
    onLoad: T.func,
    className: T.string
};

internals.Player = Styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;

internals.PlayerFrame = Styled.iframe`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
`;

internals.SoundButton = Styled(IconButton)`
    display: none;
    position: absolute !important;
    right: 12px;
    top: 12px;
    z-index: 1;
    width: 44px;
    height: 44px;
    color: #ffffff !important;
    background-color: rgba(0, 0, 0, 0.72) !important;

    &:hover {
        background-color: rgba(0, 0, 0, 0.86) !important;
    }

    @media screen and (max-width: 389px) {
        display: inline-flex;
    }
`;

internals.getYouTubeEmbedOrigin = ({
    locationOrigin = window.location?.origin,
    ...options
} = {}) => {

    if (internals.isCordovaRuntime(options)) {
        return internals.getResolvedAppOrigin({ locationOrigin, ...options });
    }

    if (/^https?:\/\//.test(locationOrigin || '')) {
        return locationOrigin;
    }

    return internals.getResolvedAppOrigin({ locationOrigin, ...options });
};

internals.isYouTubeMessageOrigin = (origin) => {

    const normalizedOrigin = String(origin || '').toLowerCase();

    return (
        normalizedOrigin === 'https://www.youtube.com' ||
        normalizedOrigin === 'https://www.youtube-nocookie.com'
    );
};

internals.parseYouTubePlayerMessage = (event) => {

    if (!internals.isYouTubeMessageOrigin(event.origin)) {
        return null;
    }

    if (typeof event.data === 'string') {
        try {
            return JSON.parse(event.data);
        }
        catch {
            return null;
        }
    }

    if (event.data && typeof event.data === 'object') {
        return event.data;
    }

    return null;
};

internals.getResolvedAppOrigin = ({
    locationOrigin = window.location?.origin,
    apiHost = process.env.API_HOST,
    appFlavour = process.env.APP_FLAVOUR,
    nodeEnv = process.env.NODE_ENV
} = {}) => {

    if (/^https?:\/\//.test(locationOrigin || '') && !/localhost/i.test(locationOrigin)) {
        return locationOrigin;
    }

    try {
        if (/^https?:\/\//.test(apiHost || '')) {
            return new URL(apiHost).origin;
        }
    }
    catch {}

    if (appFlavour === 'teams') {
        return 'https://teams.nearpeer.net';
    }

    if (nodeEnv === 'production') {
        return 'https://nearpeer.net';
    }

    return 'https://staging.nearpeer.net';
};

internals.isCordovaRuntime = ({
    hasCordova = !!window.cordova,
    isCordovaEnv = process.env.IS_CORDOVA === 'true'
} = {}) => hasCordova || isCordovaEnv;

internals.getYouTubeEmbedHost = (options = {}) => (
    internals.isCordovaRuntime(options)
        ? 'https://www.youtube.com'
        : 'https://www.youtube-nocookie.com'
);

internals.openExternalYouTubeUrl = (url) => {

    if (!url) {
        return null;
    }

    const InAppBrowser = window.cordova?.InAppBrowser;

    if (InAppBrowser) {
        return InAppBrowser.open(url, '_system');
    }

    return window.open(url, '_blank', 'noopener');
};

internals.getYouTubeVideoId = (url) => {

    if (!url) {
        return null;
    }

    const text = String(url).trim();
    const parsedUrl = (() => {

        try {
            return new URL(text);
        }
        catch {
            return null;
        }
    })();

    if (!internals.isYouTubeHost(parsedUrl?.hostname)) {
        return null;
    }

    const videoId = internals.isYouTubeShortHost(parsedUrl.hostname)
        ? parsedUrl.pathname.split('/').filter(Boolean)[0]
        : parsedUrl.searchParams.get('v') || parsedUrl.pathname.match(/^\/(?:embed|shorts)\/([a-zA-Z0-9_-]{11})(?:\/|$)/)?.[1];

    return /^[a-zA-Z0-9_-]{11}$/.test(videoId || '') ? videoId : null;
};

internals.getYouTubePlaylistId = (url) => {

    if (!url) {
        return null;
    }

    let parsedUrl;

    try {
        parsedUrl = new URL(String(url).trim());
    }
    catch {
        return null;
    }

    if (!internals.isYouTubeHost(parsedUrl.hostname)) {
        return null;
    }

    const playlistId = parsedUrl.searchParams.get('list');

    return /^[a-zA-Z0-9_-]+$/.test(playlistId || '') ? playlistId : null;
};

internals.isYouTubeHost = (hostname) => {

    const normalizedHostname = String(hostname || '').toLowerCase();

    return (
        normalizedHostname === 'youtube.com' ||
        normalizedHostname.endsWith('.youtube.com') ||
        normalizedHostname === 'youtube-nocookie.com' ||
        normalizedHostname.endsWith('.youtube-nocookie.com') ||
        internals.isYouTubeShortHost(normalizedHostname)
    );
};

internals.isYouTubeShortHost = (hostname) => {

    const normalizedHostname = String(hostname || '').toLowerCase();

    return normalizedHostname === 'youtu.be';
};

internals.getYouTubeEmbedUrl = (url, options = {}) => {

    const videoId = internals.getYouTubeVideoId(url);
    const playlistId = internals.getYouTubePlaylistId(url);

    if (!playlistId && !videoId) {
        return null;
    }

    if (internals.isCordovaRuntime(options)) {
        return internals.getYouTubeWrapperUrl({ videoId, playlistId }, options);
    }

    const params = new URLSearchParams({
        ...internals.getPlayerSettings(options),
        enablejsapi: '1',
        origin: internals.getYouTubeEmbedOrigin(options)
    });

    if (playlistId) {
        params.set('list', playlistId);

        return `${internals.getYouTubeEmbedHost(options)}/embed/videoseries?${params.toString()}`;
    }

    params.set('playlist', videoId);

    return `${internals.getYouTubeEmbedHost(options)}/embed/${videoId}?${params.toString()}`;
};

internals.getPlayerSettings = ({
    autoPlay,
    playerSettings
} = {}) => {

    if (playerSettings) {
        return playerSettings;
    }

    if (autoPlay === false) {
        return {
            autoplay: '0',
            mute: '0',
            loop: '0',
            playsinline: '1',
            controls: '1'
        };
    }

    return internals.DEFAULT_YOUTUBE_PLAYER_SETTINGS;
};

internals.getYouTubeWrapperUrl = ({ videoId, playlistId }, {
    apiHost = process.env.API_HOST,
    apiPrefix = process.env.API_PREFIX || '/api',
    playerSettings = internals.DEFAULT_YOUTUBE_PLAYER_SETTINGS
} = {}) => {

    const normalizedHost = String(apiHost || internals.getResolvedAppOrigin()).replaceAll('"', '').replace(/\/$/, '');
    const normalizedPrefix = String(apiPrefix || '').replaceAll('"', '').replace(/\/$/, '');
    const params = new URLSearchParams(playerSettings);

    if (playlistId) {
        params.set('list', playlistId);
    }

    const videoPath = videoId ? `/${encodeURIComponent(videoId)}` : '';

    return `${normalizedHost}${normalizedPrefix}/youtube/embed${videoPath}?${params.toString()}`;
};

internals.getYouTubeWatchUrl = (url) => {

    const videoId = internals.getYouTubeVideoId(url);
    const playlistId = internals.getYouTubePlaylistId(url);

    if (!videoId && !playlistId) {
        return null;
    }

    const params = new URLSearchParams();

    if (videoId) {
        params.set('v', videoId);
    }

    if (playlistId) {
        params.set('list', playlistId);
    }

    return `https://www.youtube.com/watch?${params.toString()}`;
};

internals.sendYouTubeListening = (iframe) => {

    if (!iframe?.contentWindow) {
        return false;
    }

    if (internals.isCordovaRuntime()) {
        return true;
    }

    iframe.contentWindow.postMessage(JSON.stringify({
        event: 'listening',
        id: iframe.id || 'nearpeer-youtube-player',
        channel: 'widget'
    }), '*');

    return true;
};

internals.registerYouTubePlayerControl = (iframe, command) => {

    if (!iframe) {
        return undefined;
    }

    const commands = Array.isArray(command) ? command : [command].filter(Boolean);
    const sendControlMessages = () => {

        internals.sendYouTubeListening(iframe);

        for (const playerCommand of commands) {
            internals.sendYouTubePlayerCommand(iframe, playerCommand);
        }
    };

    sendControlMessages();

    let attempts = 0;

    const intervalId = setInterval(() => {

        attempts += 1;

        if (!iframe.contentWindow || attempts >= 5) {
            clearInterval(intervalId);
            return;
        }

        sendControlMessages();
    }, 250);

    return () => clearInterval(intervalId);
};

internals.sendYouTubePlayerCommand = (iframe, command) => {

    if (!iframe?.contentWindow || !['mute', 'unMute', 'playVideo', 'pauseVideo', 'seekToStart'].includes(command)) {
        return false;
    }

    if (internals.isCordovaRuntime()) {
        iframe.contentWindow.postMessage({
            source: internals.YOUTUBE_CONTROL_MESSAGE_SOURCE,
            command
        }, '*');

        return true;
    }

    const playerCommand = command === 'seekToStart' ? 'seekTo' : command;
    const args = command === 'seekToStart' ? [0, true] : [];

    iframe.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func: playerCommand,
        args
    }), '*');

    return true;
};

YouTubePlayer.getEmbedUrl = internals.getYouTubeEmbedUrl;
YouTubePlayer.getWatchUrl = internals.getYouTubeWatchUrl;
YouTubePlayer.openExternalUrl = internals.openExternalYouTubeUrl;
YouTubePlayer.internals = internals;
